export type Lang = 'fr' | 'en'

interface ServiceItem {
  title: string
  desc: string
}

export interface Translations {
  meta: {
    title: string
    description: string
  }
  nav: {
    home: string
    services: string
    vans: string
    sponsors: string
    contact: string
  }
  quickLinks: {
    amenagement: string
    meubles: string
    location: string
    devis: string
    vente: string
    guide: string
    ebook: string
    newsletter: string
    tenteDeToit: string
  }
  linkCategories: {
    amenagementTitle: string
    amenagementSubtitle: string
    vansTitle: string
    vansSubtitle: string
    tenteDeToitTitle: string
    tenteDeToitSubtitle: string
    resourcesTitle: string
    resourcesSubtitle: string
    newsletterTitle: string
    newsletterSubtitle: string
  }
  hero: {
    badge: string
    title: string
    subtitle: string
    ctaPrimary: string
    ctaSecondary: string
  }
  appPromo: {
    badge: string
    title: string
    subtitle: string
    support: string
    cta: string
    benefit1: string
    benefit2: string
    benefit3: string
  }
  about: {
    title: string
    text: string
    text2: string
  }
  services: {
    title: string
    subtitle: string
    items: ServiceItem[]
  }
  vans: {
    title: string
    subtitle: string
    saleTitle: string
    saleDesc: string
    saleCta: string
    rentTitle: string
    rentDesc: string
    rentCta: string
  }
  ebook: {
    metaTitle: string
    metaDescription: string
    title: string
    intro: string
    planTitle: string
    planCaption: string
    comingSoon: string
    back: string
    previewLabel: string
    cta: string
    buyCta: string
    languageLabel: string
  }
  partners: {
    title: string
    subtitle: string
    slotLabel: string
    slotText: string
    collabTitle: string
    collabText: string
    collabCta: string
  }
  contact: {
    title: string
    subtitle: string
    name: string
    email: string
    phone: string
    message: string
    messagePlaceholder: string
    submit: string
    direct: string
    success: string
  }
  footer: {
    tagline: string
    quickLinksTitle: string
    contactTitle: string
    rights: string
  }
}

export const translations: Record<Lang, Translations> = {
  fr: {
    meta: {
      title: 'The Mimi Factory - Aménagement de vans & vanlife',
      description: 'The Mimi Factory conçoit des aménagements de vans sur mesure, meubles prêts à poser, guides et ebooks pour vivre la vanlife. Devis gratuit, vans à vendre et à louer.',
    },
    nav: {
      home: 'Accueil',
      services: 'Produits & Prestations',
      vans: 'Nos vans',
      sponsors: 'Partenaires',
      contact: 'Contact',
    },
    quickLinks: {
      amenagement: 'Aménagement clé en main',
      meubles: 'Meubles prêts à poser',
      location: 'Vans en location',
      devis: 'Devis sur mesure',
      vente: 'Vans à vendre',
      guide: 'Guide d\'aménagement',
      ebook: 'Ebook',
      newsletter: 'S\'inscrire à la newsletter',
      tenteDeToit: 'Tente de toit',
    },
    linkCategories: {
      amenagementTitle: 'Aménagement',
      amenagementSubtitle: 'Conversion, meubles et devis sur mesure',
      vansTitle: 'Vans',
      vansSubtitle: 'À louer ou à vendre',
      tenteDeToitTitle: 'Tente de toit',
      tenteDeToitSubtitle: 'Équipez votre van pour l\'aventure',
      resourcesTitle: 'Ressources',
      resourcesSubtitle: 'Guide et ebook pour se lancer',
      newsletterTitle: 'Newsletter',
      newsletterSubtitle: 'Rejoignez la communauté vanlife',
    },
    hero: {
      badge: 'Aménagement de vans & vanlife',
      title: 'The Mimi Factory',
      subtitle: 'Des vans aménagés avec âme, des meubles prêts à poser et des guides pour réaliser vous même votre rêve.',
      ctaPrimary: 'Demander un devis',
      ctaSecondary: 'Voir mes prestations',
    },
    appPromo: {
      badge: '🚀 NOUVEAU · APPLICATION GRATUITE',
      title: 'TON PROJET DE VAN,\nAU MÊME ENDROIT. 🚐',
      subtitle: 'Prépare, organise et fais avancer ton aménagement avec une application pensée pour ton projet de van.',
      support: 'De la première idée aux dernières finitions, garde le cap sur ton projet et avance à ton rythme.',
      cta: '🚀 DÉCOUVRIR L\'APPLICATION GRATUITEMENT',
      benefit1: '✓ Accès gratuit',
      benefit2: '✓ Pensée pour les projets de van',
      benefit3: '✓ Accessible depuis ton téléphone',
    },
    about: {
      title: 'Qui suis-je ?',
      text: 'Passionné de vanlife et d\'aménagement bois, je transforme les vans en véritables maisons roulantes, chaleureuses et fonctionnelles.',
      text2: 'Chaque projet est pensé sur mesure, avec des matériaux naturels et un vrai souci du détail, pour que votre van vous ressemble et vous suive partout sur la route.',
    },
    services: {
      title: 'Produits & Prestations',
      subtitle: 'De l\'aménagement complet à l\'ebook pratique, retrouvez tout ce qu\'il faut pour concrétiser votre projet van.',
      items: [
        {
          title: 'Aménagements sur mesure',
          desc: 'Conception et réalisation complète de l\'aménagement intérieur de votre van, pensée pour votre usage et votre budget.',
        },
        {
          title: 'Meubles prêts à poser',
          desc: 'Modules en bois prédécoupés, esthétiques et faciles à installer, pour aménager votre van sans prise de tête.',
        },
        {
          title: 'Guide d\'aménagement',
          desc: 'Un guide complet et illustré pour planifier et réussir vous-même l\'aménagement de votre van, étape par étape.',
        },
        {
          title: 'Ebook vanlife',
          desc: 'Conseils, astuces et retours d\'expérience pour vivre la vanlife sereinement, du choix du van au quotidien sur la route.',
        },
      ],
    },
    vans: {
      title: 'Mes vans',
      subtitle: 'Envie de sauter le pas ? Découvrez mes vans disponibles à la vente ou à la location.',
      saleTitle: 'Vans à vendre',
      saleDesc: 'Découvrez les vans actuellement disponibles à la vente, aménagés avec soin et prêts à prendre la route.',
      saleCta: 'Voir les vans à vendre',
      rentTitle: 'Vans en location',
      rentDesc: 'Envie de tester la vanlife avant de vous lancer ? Louez un de mes vans déjà aménagés pour votre prochaine escapade.',
      rentCta: 'Voir les vans en location',
    },
    ebook: {
      metaTitle: 'Ebooks vanlife - The Mimi Factory',
      metaDescription: 'Découvrez les ebooks pratiques de The Mimi Factory pour aménager votre van, à commencer par le plan du lit peigne.',
      title: 'Ebooks',
      intro: 'Retrouvez ici tous mes ebooks et plans pratiques pour aménager votre van, en français et en anglais.',
      planTitle: 'Plan du lit peigne',
      planCaption: 'Plan détaillé pour construire un lit peigne coulissant, une solution gain de place idéale pour votre van.',
      comingSoon: 'D\'autres ebooks arrivent très prochainement.',
      back: 'Retour à l\'accueil',
      previewLabel: 'Nouveau',
      cta: 'Voir les ebooks',
      buyCta: 'Acheter ce plan',
      languageLabel: 'Langue',
    },
    partners: {
      title: 'Partenaires & Marques',
      subtitle: 'The Mimi Factory, c\'est aussi une communauté engagée autour du voyage et de la vanlife.',
      slotLabel: 'Espace réservé',
      slotText: 'Cet encart est réservé aux marques souhaitant apparaître ici en sponsoring, avec un lien vers votre site.',
      collabTitle: 'Vous êtes une marque ?',
      collabText: 'Vous souhaitez collaborer avec The Mimi Factory ou réserver cet encart ? Écrivez-moi directement.',
      collabCta: 'Proposer une collaboration',
    },
    contact: {
      title: 'Demander un devis',
      subtitle: 'Un projet d\'aménagement en tête ? Décrivez-moi votre van et vos envies, je vous réponds rapidement.',
      name: 'Nom',
      email: 'Email',
      phone: 'Téléphone (optionnel)',
      message: 'Votre projet',
      messagePlaceholder: 'Parlez-moi de votre van, de vos envies d\'aménagement, de votre budget...',
      submit: 'Envoyer ma demande',
      direct: 'Ou écrivez-moi directement à',
      success: 'Votre messagerie va s\'ouvrir pour envoyer votre demande.',
    },
    footer: {
      tagline: 'Aménagement de vans avec âme, esprit voyage et vanlife.',
      quickLinksTitle: 'Navigation',
      contactTitle: 'Contact',
      rights: 'Tous droits réservés.',
    },
  },
  en: {
    meta: {
      title: 'The Mimi Factory - Van fit-outs & vanlife',
      description: 'The Mimi Factory designs custom van conversions, ready-to-install furniture, guides and ebooks for the vanlife journey. Free quote, vans for sale and for rent.',
    },
    nav: {
      home: 'Home',
      services: 'Products & Services',
      vans: 'Our vans',
      sponsors: 'Partners',
      contact: 'Contact',
    },
    quickLinks: {
      amenagement: 'Turnkey van conversion',
      meubles: 'Ready-to-install furniture',
      location: 'Vans for rent',
      devis: 'Custom quote',
      vente: 'Vans for sale',
      guide: 'Conversion guide',
      ebook: 'Ebook',
      newsletter: 'Subscribe to the newsletter',
      tenteDeToit: 'Roof tent',
    },
    linkCategories: {
      amenagementTitle: 'Conversion',
      amenagementSubtitle: 'Conversion, furniture and custom quotes',
      vansTitle: 'Vans',
      vansSubtitle: 'For rent or for sale',
      tenteDeToitTitle: 'Roof tent',
      tenteDeToitSubtitle: 'Gear up your van for adventure',
      resourcesTitle: 'Resources',
      resourcesSubtitle: 'Guide and ebook to get started',
      newsletterTitle: 'Newsletter',
      newsletterSubtitle: 'Join the vanlife community',
    },
    hero: {
      badge: 'Van fit-outs & vanlife',
      title: 'The Mimi Factory',
      subtitle: 'Vans converted with soul, ready-to-install furniture and guides to fully live your vanlife adventure.',
      ctaPrimary: 'Request a quote',
      ctaSecondary: 'See my services',
    },
    appPromo: {
      badge: '🚀 NEW · FREE APP',
      title: 'YOUR VAN PROJECT,\nALL IN ONE PLACE. 🚐',
      subtitle: 'Prepare, organise and move your conversion forward with an app built for your van project.',
      support: 'From the first idea to the last details, stay on track and progress at your own pace.',
      cta: '🚀 DISCOVER THE APP FOR FREE',
      benefit1: '✓ Free access',
      benefit2: '✓ Built for van projects',
      benefit3: '✓ Available on your phone',
    },
    about: {
      title: 'About me',
      text: 'Passionate about vanlife and woodworking, I turn vans into warm, functional homes on wheels.',
      text2: 'Every project is custom-made, using natural materials and real attention to detail, so your van truly feels like yours on every road.',
    },
    services: {
      title: 'Products & Services',
      subtitle: 'From full conversions to a practical ebook, find everything you need to bring your van project to life.',
      items: [
        {
          title: 'Custom conversions',
          desc: 'Full design and build of your van\'s interior, tailored to your use and your budget.',
        },
        {
          title: 'Ready-to-install furniture',
          desc: 'Pre-cut, good-looking wooden modules that are easy to install, so you can fit out your van hassle-free.',
        },
        {
          title: 'Conversion guide',
          desc: 'A complete, illustrated guide to plan and successfully convert your own van, step by step.',
        },
        {
          title: 'Vanlife ebook',
          desc: 'Tips, tricks and real experience to live vanlife with peace of mind, from choosing your van to daily life on the road.',
        },
      ],
    },
    vans: {
      title: 'My vans',
      subtitle: 'Ready to take the leap? Discover my vans currently for sale or for rent.',
      saleTitle: 'Vans for sale',
      saleDesc: 'Discover the vans currently for sale, carefully converted and ready to hit the road.',
      saleCta: 'See vans for sale',
      rentTitle: 'Vans for rent',
      rentDesc: 'Want to try vanlife before committing? Rent one of my already converted vans for your next getaway.',
      rentCta: 'See vans for rent',
    },
    ebook: {
      metaTitle: 'Vanlife ebooks - The Mimi Factory',
      metaDescription: 'Discover The Mimi Factory practical ebooks to convert your van, starting with the comb bed plan.',
      title: 'Ebooks',
      intro: 'Find here all my ebooks and practical plans to convert your van, in French and in English.',
      planTitle: 'Comb bed plan',
      planCaption: 'Detailed plan to build a sliding comb bed, a great space-saving solution for your van.',
      comingSoon: 'More ebooks are coming very soon.',
      back: 'Back to home',
      previewLabel: 'New',
      cta: 'See ebooks',
      buyCta: 'Buy this plan',
      languageLabel: 'Language',
    },
    partners: {
      title: 'Partners & Brands',
      subtitle: 'The Mimi Factory is also a community built around travel and vanlife.',
      slotLabel: 'Reserved slot',
      slotText: 'This spot is reserved for brands who want to appear here as a sponsor, with a link to your site.',
      collabTitle: 'Are you a brand?',
      collabText: 'Want to collaborate with The Mimi Factory or book this spot? Write to me directly.',
      collabCta: 'Propose a collaboration',
    },
    contact: {
      title: 'Request a quote',
      subtitle: 'Have a conversion project in mind? Tell me about your van and your ideas, I\'ll reply quickly.',
      name: 'Name',
      email: 'Email',
      phone: 'Phone (optional)',
      message: 'Your project',
      messagePlaceholder: 'Tell me about your van, your ideas for the conversion, your budget...',
      submit: 'Send my request',
      direct: 'Or write to me directly at',
      success: 'Your mail client will open to send your request.',
    },
    footer: {
      tagline: 'Van conversions with soul, travel and vanlife spirit.',
      quickLinksTitle: 'Navigation',
      contactTitle: 'Contact',
      rights: 'All rights reserved.',
    },
  },
}
